import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ChevronLeft, ChevronRight, CalendarDays, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtDateShort } from "@/lib/format";
import { PageHeader } from "@/components/Page";

function mondayOf(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function iso(d) { return d.toISOString().slice(0, 10); }

const DEPT_COLORS = {
  "küche": { dot: "bg-sky-400", card: "bg-sky-50 border-sky-200" },
  "reinigung": { dot: "bg-green-400", card: "bg-green-50 border-green-200" },
  "service": { dot: "bg-yellow-400", card: "bg-yellow-50 border-yellow-200" },
};
const FALLBACK_COLORS = [
  { dot: "bg-purple-400", card: "bg-purple-50 border-purple-200" },
  { dot: "bg-pink-400", card: "bg-pink-50 border-pink-200" },
  { dot: "bg-orange-400", card: "bg-orange-50 border-orange-200" },
  { dot: "bg-teal-400", card: "bg-teal-50 border-teal-200" },
];

export default function Schichtplan() {
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [company, setCompany] = useState(null);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [deptFilter, setDeptFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_ids: [], dates: [iso(new Date())], start: "08:00", end: "16:00", note: "" });

  const load = async () => {
    const [s, e, d, c] = await Promise.all([api.get("/shifts"), api.get("/employees"), api.get("/departments"), api.get("/company")]);
    setShifts(s.data || []);
    setEmployees((e.data || []).filter((u) => u.role === "employee"));
    setDepartments(d.data || []);
    setCompany(c.data || null);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/availability", { params: { week_start: iso(weekStart) } }).then((r) => setAvailability(r.data || [])).catch(() => {});
  }, [weekStart]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(weekStart);
    dt.setDate(dt.getDate() + i);
    return iso(dt);
  });

  const inDept = (u) => deptFilter === "all" || (u.department_ids || []).includes(deptFilter);
  const empById = Object.fromEntries(employees.map((u) => [u.user_id, u]));
  const visibleEmployees = employees.filter(inDept);
  const visibleIds = new Set(visibleEmployees.map((u) => u.user_id));

  const deptStyle = (deptId) => {
    const idx = departments.findIndex((d) => d.id === deptId);
    if (idx < 0) return null;
    return DEPT_COLORS[(departments[idx].name || "").trim().toLowerCase()] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
  };
  const primaryDept = (u) => (u?.department_ids || []).find((id) => departments.some((d) => d.id === id));

  const empGroups = (() => {
    const groups = departments.map((d) => ({
      key: d.id, label: d.name, dot: deptStyle(d.id)?.dot,
      emps: visibleEmployees.filter((u) => primaryDept(u) === d.id),
    }));
    const noDept = visibleEmployees.filter((u) => !primaryDept(u));
    if (noDept.length) groups.push({ key: "none", label: "Ohne Abteilung", dot: null, emps: noDept });
    return groups.filter((g) => g.emps.length > 0);
  })();

  const submit = async () => {
    if (!form.user_ids.length) return toast.error("Bitte mindestens einen Mitarbeiter wählen");
    if (!form.dates.length) return toast.error("Bitte mindestens ein Datum wählen");
    const jobs = [];
    let skipped = 0;
    for (const date of form.dates) {
      for (const uid of form.user_ids) {
        if (blockEnabled && availability.some((a) => a.user_id === uid && a.date === date)) { skipped++; continue; }
        jobs.push(api.post("/shifts", { user_id: uid, date, start: form.start, end: form.end, note: form.note }));
      }
    }
    if (!jobs.length) return toast.error("Alle Kombinationen blockiert (nicht verfügbar)");
    const results = await Promise.allSettled(jobs);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    if (ok) toast.success(`${ok} Schicht${ok > 1 ? "en" : ""} angelegt${skipped ? `, ${skipped} übersprungen (nicht verfügbar)` : ""}`);
    else toast.error("Keine Schicht angelegt");
    setForm({ ...form, user_ids: [] });
    setOpen(false);
    load();
  };

  const del = async (id) => {
    try { await api.delete(`/shifts/${id}`); toast.success("Schicht gelöscht"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const reassign = async (shift, uid) => {
    if (uid === shift.user_id) return;
    try { await api.patch(`/shifts/${shift.id}`, { user_id: uid }); toast.success("Mitarbeiter geändert"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleDate = (d) => setForm((f) => ({ ...f, dates: f.dates.includes(d) ? f.dates.filter((x) => x !== d) : [...f.dates, d].sort() }));
  const dayLabel = (d) => new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

  const deptOrder = (uid) => {
    const dId = primaryDept(empById[uid]);
    const idx = departments.findIndex((d) => d.id === dId);
    return idx < 0 ? departments.length : idx;
  };
  const shiftsFor = (d) => shifts
    .filter((s) => s.date === d && (deptFilter === "all" || visibleIds.has(s.user_id)))
    .sort((a, b) => deptOrder(a.user_id) - deptOrder(b.user_id) || (a.user_name || "").localeCompare(b.user_name || ""));
  const unavailFor = (d) => availability.filter((a) => a.date === d && (deptFilter === "all" || visibleIds.has(a.user_id)));
  const availLabel = (a) => a.all_day ? "ganztägig" : `${a.start || "?"}–${a.end || "?"}`;

  const conflicts = availability.filter((a) => form.dates.includes(a.date) && form.user_ids.includes(a.user_id));
  const blockEnabled = !!company?.shift_block_unavailable;
  const blocked = blockEnabled && form.user_ids.length > 0 && form.dates.length > 0 &&
    form.dates.every((date) => form.user_ids.every((uid) => availability.some((a) => a.user_id === uid && a.date === date)));

  return (
    <div>
      <PageHeader
        title="Schichtplanung"
        subtitle="Plane Einsätze in der Wochenansicht. Rot = Mitarbeiter nicht verfügbar."
        testid="schichtplan-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="shift-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Schicht</Button></DialogTrigger>
            <DialogContent data-testid="shift-dialog">
              <DialogHeader><DialogTitle>Schicht anlegen</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Mitarbeiter</Label>
                    <button type="button" className="text-xs text-slate-500 hover:text-slate-800" data-testid="shift-select-all" onClick={() => setForm({ ...form, user_ids: form.user_ids.length === visibleEmployees.length ? [] : visibleEmployees.map((u) => u.user_id) })}>
                      {form.user_ids.length === visibleEmployees.length && visibleEmployees.length > 0 ? "Alle abwählen" : "Alle wählen"}
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto pr-1 space-y-3">
                    {empGroups.map((grp) => (
                      <div key={grp.key} className="space-y-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          {grp.dot && <span className={`h-2.5 w-2.5 rounded-full ${grp.dot}`} />} {grp.label}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {grp.emps.map((u) => {
                            const checked = form.user_ids.includes(u.user_id);
                            const unavail = availability.some((a) => a.user_id === u.user_id && form.dates.includes(a.date));
                            return (
                              <label key={u.user_id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${checked ? "border-slate-300 bg-slate-50" : "border-slate-200"}`} data-testid={`shift-emp-${u.user_id}`}>
                                <Checkbox checked={checked} onCheckedChange={(v) => setForm({ ...form, user_ids: v ? [...form.user_ids, u.user_id] : form.user_ids.filter((x) => x !== u.user_id) })} />
                                <span className="text-sm text-slate-700 truncate">{u.name}</span>
                                {unavail && <span className="ml-auto text-[10px] text-rose-500 font-semibold shrink-0">n.v.</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Datum(e)</Label>
                    <div className="flex gap-3">
                      <button type="button" data-testid="shift-dates-week" className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setForm({ ...form, dates: [...days] })}>Ganze Woche</button>
                      <button type="button" data-testid="shift-dates-workdays" className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setForm({ ...form, dates: days.slice(0, 5) })}>Mo–Fr</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5" data-testid="shift-date-grid">
                    {days.map((d) => {
                      const sel = form.dates.includes(d);
                      return (
                        <button key={d} type="button" data-testid={`shift-date-${d}`} onClick={() => toggleDate(d)}
                          className={`text-xs rounded-lg px-2 py-1.5 border capitalize ${sel ? "brand-bg text-white border-transparent" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                          {dayLabel(d)}
                        </button>
                      );
                    })}
                  </div>
                  <Input type="date" data-testid="shift-date" value="" onChange={(e) => e.target.value && toggleDate(e.target.value)} />
                  {form.dates.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="shift-dates-selected">
                      {form.dates.map((d) => (
                        <span key={d} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-full px-2 py-0.5 capitalize">
                          {dayLabel(d)}
                          <button type="button" onClick={() => toggleDate(d)} className="text-slate-400 hover:text-rose-500">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {conflicts.length > 0 && (
                  <div className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 border ${blocked ? "text-rose-800 bg-rose-100 border-rose-300" : "text-rose-700 bg-rose-50 border-rose-200"}`} data-testid="shift-unavailable-warning">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span><strong>Nicht verfügbar</strong> an ausgewählten Tagen: {[...new Set(conflicts.map((a) => empById[a.user_id]?.name).filter(Boolean))].join(", ")}.{blocked ? " Anlegen blockiert (siehe Einstellungen)." : blockEnabled ? " Diese werden beim Anlegen übersprungen." : ""}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Von</Label><Input type="time" data-testid="shift-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Bis</Label><Input type="time" data-testid="shift-end" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Notiz</Label><Input data-testid="shift-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" /></div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={form.user_ids.length === 0 || form.dates.length === 0 || blocked} data-testid="shift-submit" className="brand-bg text-white hover:opacity-90">Anlegen ({form.user_ids.length * form.dates.length})</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm font-medium text-slate-700">Woche ab {fmtDateShort(days[0])}</span>
        <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
        <div className="ml-auto w-52">
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger data-testid="dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Abteilungen</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {departments.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4" data-testid="dept-legend">
          {departments.map((d) => (
            <span key={d.id} className="flex items-center gap-1.5 text-xs text-slate-600" data-testid={`legend-${d.id}`}>
              <span className={`h-3 w-3 rounded-full ${deptStyle(d.id)?.dot}`} /> {d.name}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d) => {
          const unavail = unavailFor(d);
          return (
            <Card key={d} className="p-3 border-slate-200 rounded-xl min-h-[140px]" data-testid={`day-${d}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" /> {fmtDateShort(d)}
              </div>
              <div className="space-y-2">
                {shiftsFor(d).map((s) => {
                  const st = deptStyle(primaryDept(empById[s.user_id]));
                  return (
                  <div key={s.id} className={`group text-xs rounded-lg p-2 border ${st ? st.card : "bg-slate-50 border-slate-200"}`} data-testid={`shift-${s.id}`}>
                    <Select value={s.user_id} onValueChange={(uid) => reassign(s, uid)}>
                      <SelectTrigger data-testid={`shift-reassign-${s.id}`} title="Mitarbeiter tauschen" className="h-auto py-0.5 px-1 -mx-1 border-0 shadow-none bg-transparent font-medium text-slate-900 text-xs focus:ring-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-40">
                        <span className="truncate">{s.user_name}</span>
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="font-mono text-slate-600">{s.start}–{s.end}</span>
                      <button onClick={() => del(s.id)} data-testid={`shift-delete-${s.id}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  );
                })}
              </div>
              {unavail.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1" data-testid={`unavail-${d}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Nicht verfügbar</div>
                  {unavail.map((a) => (
                    <div key={a.id} className="text-xs text-rose-700 truncate" data-testid={`unavail-item-${a.id}`}>
                      {empById[a.user_id]?.name || a.user_name} <span className="text-rose-400">({availLabel(a)})</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
